<template>
    <v-tooltip v-if="isLoading" :location="location" :text="text" class="mb-0 pb-0" :disabled="disableTooltip">
        <template #activator="{ props }">
            <v-progress-circular
                :color="color"
                :size="spinnerTranslation"
                :width="progressWidth"
                indeterminate
                v-bind="props"
            >
                <template #default>
                    <slot name="activator" v-bind:props="props">
                        <v-btn v-if="isButton" :color="color" :disabled="!enabledOnLoading"
                               :icon="loadingIcon ? loadingIcon : icon"
                               :size="size" :variant="variant" :class="cursorClass"
                               v-bind="props" @click="emitClick"/>
                        <div v-else-if="isInitial" v-bind="props" :data-letters="initials" @click="emitClick" :class="classes"></div>
                        <v-icon v-else :color="color" :disabled="!enabledOnLoading" :size="size" v-bind="props" :class="cursorClass"
                                @click="emitClick">{{ loadingIcon ? loadingIcon : icon }}
                        </v-icon>
                    </slot>
                </template>
            </v-progress-circular>
        </template>
        <template #default>
            <slot name="content">{{text}}</slot>
        </template>
    </v-tooltip>
    <v-tooltip v-else :location="location" class="mb-0 pb-0" :disabled="disableTooltip">
        <template v-slot:activator="{ props }">
            <slot name="activator" v-bind:props="props">
                <v-btn v-if="isButton" :color="color" :disabled="disabled" :icon="icon" :size="size" :class="classes"
                       :variant="variant"
                       v-bind="props" @click="emitClick" />
                <div v-else-if="isInitial" v-bind="props" :data-letters="initials" @click="emitClick" :class="classes" ></div>
                <v-icon v-else :color="color" :disabled="disabled" :size="size" v-bind="props" @click="emitClick" :class="classes">
                    {{icon}}
                </v-icon>
            </slot>
        </template>
        <template #default>
            <slot name="content">
                {{text}}
            </slot>
        </template>
    </v-tooltip>
</template>


<script lang="ts">

import type {PropType, Ref} from "vue";
import {computed, defineComponent, ref, watch} from "vue";
import {TColor} from "@/components/generics/types/TColor";
import {TLocation} from "@/components/dds/KToolTip/types/TLocation";
import {TBtnVariant} from "@/components/generics/types/TBtnVariant";
import {KPMGDDSTheme} from "@/styles/dds-vuetify-theme";


export default defineComponent({
    emits: ['click'],
    props: {
        classes: {
            type: Array<String>,
            default: []
        },
        text: {
            type: String,
            default: ''
        },
        color: {
            type: String as PropType<TColor>,
            default: 'kpmgError-900'
        },
        icon: {
            type: String
        },
        size: {
            type: String,
            default: 'small'
        },
        loadingIcon: {
            type: String
        },
        location: {
            type: String as PropType<TLocation>,
            default: 'bottom'
        },
        isInitial: {
            type: Boolean,
            default: false
        },
        initials: {
            type: String,
            default: ""
        },
        initialTextColor: {
            type: String as PropType<TColor>,
            default: 'surface'
        },
        isButton: {
            type: Boolean,
            default: false
        },
        isLoading: {
            type: Boolean,
            default: false
        },
        enabledOnLoading: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        variant: {
            type: String as PropType<TBtnVariant>,
            default: 'elevated'
        },
        progressSize: {
            type: Number,
        },
        progressWidth: {
            type: Number,
            default: 1
        },
        loadingCursor: {
            type: String,
            default: "cursor-pointer"
        },
        disableTooltip: {
            type: Boolean,
            default: false
        }

    },
    setup(props, {emit}) {
        const tooltipState: Ref<boolean> = ref(false);

        const resolvedBackgroundColor = ref(KPMGDDSTheme.colors[props.color]);
        const resolvedTextColor = ref(KPMGDDSTheme.colors[props.initialTextColor]);
        const emitClick = () => {
            if ((props.isLoading && props.enabledOnLoading) || !props.isLoading) {
                emit('click')
            }
        }
        const cursorClass = computed(()=>{
            if (props.isLoading && !props.enabledOnLoading){
                return [...props.classes, props.loadingCursor]
            }
            return [...props.classes, "cursor-pointer"]
        })
        const sizeTranslation = computed(()=>{
            switch(props.size){
                case "x-small":
                    return "1.9em";
                case "small":
                    return "2.4em";
                case "default":
                    return "3em";
                case "large":
                    return "2.2em";
                case "x-large":
                    return "2.2em";
                default:
                    return props.size
            }
        })
        const textSizeTranslation = computed(()=>{
            switch(props.size){
                case "x-small":
                    return "0.9em";
                case "small":
                    return "1.12em";
                case "default":
                    return "1.12em";
                case "large":
                    return "2em";
                case "x-large":
                    return "2.3em";
                default:
                    return "1em"
            }
        })
        const spinnerTranslation = computed(()=>{
            if (props.progressSize != undefined){
                return props.progressSize
            }
            switch(props.size){
                case "x-small":
                    return 41;
                case "small":
                    return 52;
                case "default":
                    return 59;
                case "large":
                    return 72;
                case "x-large":
                    return 85;
                default:
                    return 50
            }
        })
        watch(props, ()=>{
            resolvedBackgroundColor.value = KPMGDDSTheme.colors[props.color]
            resolvedTextColor.value = KPMGDDSTheme.colors[props.initialTextColor]
        })
        return {
            // ref
            resolvedBackgroundColor,
            resolvedTextColor,
            tooltipState,
            cursorClass,
            sizeTranslation,
            textSizeTranslation,
            spinnerTranslation,
            // f=>
            emitClick
        }
    },
})
</script>
<style scoped>
[data-letters]:before {
    content:attr(data-letters);
    display:inline-block;
    font-size:v-bind(textSizeTranslation);
    width:v-bind(sizeTranslation);
    height:v-bind(sizeTranslation);
    line-height:v-bind(sizeTranslation);
    text-align:center;
    border-radius:50%;
    background: v-bind(resolvedBackgroundColor);
    vertical-align:middle;
    color:v-bind(resolvedTextColor);
    justify-self: center;
}
</style>